import { useNavigate } from 'react-router-dom';
import { categoryData, statusLabels } from '@/mocks/categories';
import BottomNav from '@/components/BottomNav';
import { vendors } from '@/mocks/vendors';

const myStatuses: Record<string, { status: string; nextAction: string }> = {
  dress: { status: 'not_started', nextAction: '예복 기준 진단 시작하기' },
  jewelry: { status: 'has_candidate', nextAction: '추천 업체와 비교해보기' },
  hanbok: { status: 'not_started', nextAction: '한복 기준 진단 시작하기' },
};

const guideCards = [
  { title: '예복, 무엇부터 보면 좋을까요?', category: 'dress', icon: 'ri-shirt-line' },
  { title: '예물, 무엇부터 정하면 좋을까요?', category: 'jewelry', icon: 'ri-vip-diamond-line' },
  { title: '한복, 누구부터 입을지 정해볼까요?', category: 'hanbok', icon: 'ri-flower-line' },
];

export default function Home() {
  const navigate = useNavigate();

  const progressDone = Object.values(myStatuses).filter((s) => s.status !== 'not_started').length;
  const progressTotal = 3;

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      <div className="flex-1 overflow-y-auto pb-2">
        {/* Dashboard */}
        <div className="bg-navy px-5 pt-8 pb-6 rounded-b-[28px]">
          <div className="mb-6">
            <p className="text-cream-dark text-sm mb-1">안녕하세요, 지혜님</p>
            <h1 className="text-white text-lg font-bold leading-snug">
              아직 예복과 한복이
              <br />
              정리되지 않았어요
            </h1>
            <p className="text-beige text-sm mt-2">오늘은 예복 기준부터 잡아볼까요?</p>
          </div>

          <div className="bg-white/10 backdrop-blur rounded-2xl p-4 border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <span className="text-cream text-xs">준비 진행률</span>
              <span className="text-softgold text-xs font-bold">{progressDone}/{progressTotal}</span>
            </div>
            <div className="h-2 bg-white/15 rounded-full overflow-hidden">
              <div
                className="h-full bg-softgold rounded-full transition-all duration-500"
                style={{ width: `${(progressDone / progressTotal) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Category Cards */}
        <div className="px-5 mt-5 space-y-3">
          {Object.entries(categoryData).map(([key, cat]) => {
            const myStatus = myStatuses[key];
            const statusInfo = statusLabels[myStatus.status];
            return (
              <button
                key={key}
                onClick={() => navigate(`/guide/${key}`)}
                className="w-full bg-white rounded-2xl p-4 border border-cream-dark flex items-center gap-4 text-left cursor-pointer hover:shadow-sm transition-shadow"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${cat.color}12`, color: cat.color }}
                >
                  <i className={`${cat.icon} text-xl`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-navy text-sm">{cat.name}</span>
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                      style={{
                        backgroundColor: statusInfo.badgeColor,
                        color: statusInfo.color,
                      }}
                    >
                      {statusInfo.label}
                    </span>
                  </div>
                  <p className="text-beige-dark text-xs truncate">{myStatus.nextAction}</p>
                </div>
                <div className="w-6 h-6 flex items-center justify-center text-beige-dark shrink-0">
                  <i className="ri-arrow-right-s-line" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Quick Quote CTA */}
        <div className="px-5 mt-5">
          <button
            onClick={() => navigate('/quote')}
            className="w-full bg-navy text-white rounded-2xl p-5 flex items-center justify-between cursor-pointer hover:bg-navy-light transition-colors"
          >
            <div>
              <p className="font-bold text-base">3개 한 번에 견적 받기</p>
              <p className="text-cream-dark text-xs mt-1">조건에 맞는 업체가 제안을 보내드려요</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0">
              <i className="ri-mail-send-line text-white" />
            </div>
          </button>
        </div>

        {/* Today's Recommendations */}
        <div className="px-5 mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-navy font-bold text-base">오늘의 추천 업체</h3>
            <button
              onClick={() => navigate('/vendors')}
              className="text-beige-dark text-xs cursor-pointer hover:text-navy transition-colors"
            >
              전체보기
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
            {vendors.slice(0, 3).map((vendor) => (
              <button
                key={vendor.id}
                onClick={() => navigate(`/vendor/${vendor.id}`)}
                className="shrink-0 w-[160px] bg-white rounded-xl border border-cream-dark overflow-hidden text-left cursor-pointer hover:shadow-sm transition-shadow"
              >
                <div className="w-full h-[110px] overflow-hidden">
                  <img
                    src={vendor.image}
                    alt={vendor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-3">
                  <p className="font-semibold text-navy text-sm truncate">{vendor.name}</p>
                  <p className="text-beige-dark text-xs mt-0.5">{vendor.region}</p>
                  <div className="flex items-center gap-1 mt-1.5">
                    <span
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-cream text-navy"
                    >
                      {vendor.priceRange}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Photo Reviews Moodboard */}
        <div className="px-5 mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-navy font-bold text-base">실제 후기 사진</h3>
            <button
              onClick={() => navigate('/reviews')}
              className="text-beige-dark text-xs cursor-pointer hover:text-navy transition-colors"
            >
              전체보기
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {vendors.slice(0, 4).map((vendor) => (
              <button
                key={vendor.id}
                onClick={() => navigate(`/vendor/${vendor.id}`)}
                className="relative rounded-xl overflow-hidden cursor-pointer text-left"
              >
                <div className="w-full h-[140px]">
                  <img
                    src={vendor.image}
                    alt={vendor.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2">
                  <p className="text-white text-xs font-medium truncate">{vendor.name}</p>
                  <p className="text-white/70 text-[10px] mt-0.5">
                    {categoryData[vendor.category as keyof typeof categoryData].name}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Preparation Guide */}
        <div className="px-5 mt-8 mb-6">
          <h3 className="text-navy font-bold text-base mb-4">준비 가이드</h3>
          <div className="space-y-3">
            {guideCards.map((guide) => {
              const cat = categoryData[guide.category as keyof typeof categoryData];
              return (
                <button
                  key={guide.category}
                  onClick={() => navigate(`/guide/${guide.category}`)}
                  className="w-full bg-white rounded-xl p-4 border border-cream-dark flex items-center gap-3 text-left cursor-pointer hover:shadow-sm transition-shadow"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${cat.color}12`, color: cat.color }}
                  >
                    <i className={`${guide.icon} text-lg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-navy text-sm font-medium truncate">{guide.title}</p>
                    <p className="text-beige-dark text-xs mt-0.5">기준 정리하고 추천받기</p>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center text-beige-dark shrink-0">
                    <i className="ri-arrow-right-s-line" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}